import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowRight } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const FAQ = [
  {
    q: "In che cosa consiste una consulenza con Teresa?",
    a: "Ogni consulenza parte da un colloquio conoscitivo (spesso gratuito, tramite Calendly) in cui capiamo insieme il bisogno della famiglia. A seguire viene costruito un percorso personalizzato — a domicilio, in studio o online — con obiettivi chiari e restituzioni educative regolari."
  },
  {
    q: "Quanto costa una consulenza?",
    a: "Il costo di ogni percorso viene concordato di volta in volta con la famiglia, in base alle esigenze specifiche (età del bambino, durata, frequenza, tipologia di consulenza). Dopo il primo contatto riceverai un preventivo personalizzato."
  },
  {
    q: "I percorsi sono adatti anche se il mio bambino è molto piccolo?",
    a: "Sì. Mi occupo di bambini dai primi giorni di vita fino ai 6 anni. Ogni intervento è pensato per la fascia d'età e i bisogni specifici, con particolare attenzione ai neonati e alla fascia 0–36 mesi."
  },
  {
    q: "Come funziona la Cura Educativa a Domicilio?",
    a: "Entro nella tua casa con un progetto educativo condiviso: osservo, propongo attività coerenti con l'età e il temperamento del bambino, sostengo le routine (sonno, pappa, gioco) e ti restituisco un quadro chiaro dei progressi. Gli slot sono prenotabili fino a 3 ore consecutive tramite Calendly."
  },
  {
    q: "Posso disdire un appuntamento?",
    a: "Sì. Fino a 48 ore prima puoi disdire o riprogrammare senza costi. Tra 48 e 24 ore prima è possibile solo la riprogrammazione. Entro 24 ore o in caso di mancata presentazione, l'incontro si considera erogato. Dettagli nei Termini d'uso."
  },
  {
    q: "Posso pagare online?",
    a: "Al momento la prenotazione avviene tramite Calendly o modulo di contatto. Le modalità di pagamento (bonifico, contanti, PayPal) vengono comunicate a conferma dell'appuntamento."
  },
  {
    q: "Fai formazione anche per tate e babysitter?",
    a: "Sì. All'interno di T.A.T.A. Academy offro percorsi professionalizzanti (\"Become Tata like a Pro\"), consulenze individuali per tate, e un corso specialistico sul sonno dei piccoli. I dettagli nella sezione dedicata alle Tate."
  },
  {
    q: "In cosa consiste il Metodo T.A.T.A.®?",
    a: "È l'approccio educativo che ho sviluppato in oltre 23 anni di lavoro. Si fonda su 7 pilastri (lo Stare, il Benessere Globale, il Tempo, l'Ambiente, il Corpo, l'Osservazione, l'Autonomia Relazionale) e trova applicazione pratica nel Protocollo T.A.T.A.®. Trovi tutto nella pagina Metodo T.A.T.A.."
  },
  {
    q: "Posso organizzare una festa di compleanno con te?",
    a: "Certo! Organizzo feste ed eventi per bambini da 0 a 6 anni, in spazi privati o sale convenzionate. Offro anche il noleggio di stoviglie riciclabili di qualità. Compila il modulo nella pagina Feste per un preventivo."
  },
  {
    q: "Come posso ricevere la guida gratuita?",
    a: "Vai nella pagina Risorse (o clicca sul pulsante \"Guida gratuita\" nel menu floating in basso a destra), compila il modulo e riceverai il PDF direttamente via email."
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-page">
      <section className="pt-32 md:pt-40 pb-16 px-6 md:px-10 max-w-[1400px] mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Domande frequenti</p>
        <h1 className="font-display text-[14vw] md:text-[11vw] lg:text-[13rem] leading-[0.82] text-[#3D2140] mb-6">
          <RevealText text={["Le tue", <span key="1" className="text-[#FF2768]">domande.</span>]} stagger={0.14} />
        </h1>
        <p className="max-w-2xl text-lg text-[#3D2140]/70 leading-relaxed mb-16 font-editorial text-2xl italic">
          Risposte alle domande che mi vengono fatte più spesso da genitori, tate e professioniste dell'infanzia.
        </p>

        <div className="space-y-3">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="border-b border-[#3D2140]/15"
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 md:py-8 text-left group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <h3 className="font-display text-2xl md:text-4xl text-[#3D2140] group-hover:text-[#FF2768] transition-colors leading-tight">
                    {item.q}
                  </h3>
                  <span className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition-all ${isOpen ? "bg-[#FF2768] text-white rotate-180" : "bg-[#81C3C9]/25 text-[#3D2140]"}`}>
                    {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-8 md:pb-10 md:pr-16 text-[#3D2140]/80 leading-relaxed text-lg">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20 p-10 md:p-16 rounded-[40px] bg-[#81C3C9]/25 border border-[#81C3C9]/40 text-center">
          <p className="font-editorial text-[#FF2768] text-xl mb-4">— Hai una domanda diversa?</p>
          <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
            Scrivimi.<br/><span className="text-[#FF2768]">Rispondo io stessa.</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/prenota" data-testid="faq-cta-booking" className="btn-tv-primary">
              Contattami <ArrowRight size={16} />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="faq-cta-whatsapp" className="btn-tv-outline">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
